import { CONTINGENT_VALUE_TYPES, DEFAULT_VALUE_TYPE, UNCATEGORIZED } from '../billingCategories';

// Pure logic behind the Milestone Tracker -- bucketing, period keys, aging and the burn-down series.
// Split out of the page file only so the arithmetic is unit-testable without rendering the grid;
// the page itself stays one file per CLAUDE.md Section 6.

export const MILESTONE_STATUSES = ['Not Started', 'In Progress', 'Delivered', 'Accepted', 'Billed', 'Paid'];
export const DONE_STATUSES = new Set(['Delivered', 'Accepted', 'Billed', 'Paid']);

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Every date here is read by slicing the ISO string, never by constructing a Date and calling local
// getters: the API sends date-only values as UTC midnight (ADR 0009), which read back a day early
// anywhere west of UTC. The previous version of this page used `new Date(d).setHours(0,0,0,0)` and
// had exactly that off-by-one in its overdue test.
export function ymd(d) {
  return d ? String(d).slice(0, 10) : '';
}

export function todayYmd(now = new Date()) {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${day}`;
}

export function daysUntil(dateStr, today) {
  const s = ymd(dateStr);
  if (!s || !today) return null;
  return Math.round((Date.parse(`${s}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
}

// On-screen date format (Judy, 2026-09-16): month and year only. The full date stays in the DOM as
// a title attribute and in the Excel export, so the day isn't lost -- just off the grid.
export function monthYearLabel(d) {
  const s = ymd(d);
  return s ? `${MONTHS[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}` : '—';
}

export function yearOf(d) {
  const s = ymd(d);
  return s ? s.slice(0, 4) : null;
}

// A milestone with no billing category reads as Uncategorized rather than being silently dropped --
// only reachable for rows created after the backfill, which set every existing one to PS.
export function categoryOf(m) {
  return m.billingCategory || UNCATEGORIZED;
}

// "SC - Charleston - Aumentum Upgrade" -> "Aumentum Upgrade", now that State and County are their
// own columns (Judy, 2026-09-16 -- the account name was also printing under every project name).
// Only strips a prefix that actually matches this project's account, so the names that don't follow
// the convention ("MN - MNCCC Migrations", "Palm Beach - Core Work") keep every word.
// The account IS the county (Judy, 2026-09-16), so under a "County" header "Charleston County"
// says it twice. Trimmed for display only -- the account name itself is untouched, and the Excel
// export and the hover title still carry it in full.
export function countyLabel(project) {
  return (project?.account?.name || '').replace(/\s+County$/i, '');
}

export function shortProjectName(project) {
  const full = project?.name || '';
  const state = project?.account?.state || '';
  const county = (project?.account?.name || '').replace(/\s+County$/i, '');
  if (!state && !county) return full;
  const escape = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, (ch) => `\\${ch}`);
  const prefix = [state, county].filter(Boolean).map(escape).join('\\s*-\\s*');
  const stripped = full.replace(new RegExp(`^\\s*${prefix}\\s*-\\s*`, 'i'), '');
  return stripped.trim() || full;
}

export function isDone(m) {
  return !!(m.status && DONE_STATUSES.has(m.status));
}

// FCST vs ACT, in Judy's words: ACT is anything completed, FCST is anything future or not yet
// completed. Derived from status rather than stored, so it can never disagree with the status badge.
export function trackOf(m) {
  return isDone(m) ? 'ACT' : 'FCST';
}

// Which date puts a milestone in a period. A completed one lands where it actually happened; an
// open one lands where it's forecast to. A completed milestone with no actual date falls back to its
// target rather than dropping out of the grid entirely.
export function periodDateOf(m) {
  return isDone(m) ? (m.actualDate || m.targetDate) : m.targetDate;
}

// Mutually exclusive, so the cards never double-count: done wins over any date, and the day bands
// are checked in order. 'unscheduled' is a real state here -- 59 of 239 milestones had no target
// date when this was built, and they belong in the total even though they can't be aged.
export function milestoneBucket(m, today) {
  if (isDone(m)) return 'complete';
  const d = daysUntil(m.targetDate, today);
  if (d == null) return 'unscheduled';
  if (d < 0) return 'overdue';
  if (d <= 30) return 'due30';
  if (d <= 60) return 'due60';
  if (d <= 90) return 'due90';
  return 'later';
}

// Sort key for the Target column (Judy, 2026-09-16). Ascending puts the most urgent first --
// overdue, then 0-30, 31-60, 61-90, then everything further out -- rather than plain date order,
// which buries a late item among old completed ones. Completed work sorts below the open buckets
// (it needs no chasing) and undated last (nothing to rank it by). Within a bucket it's still date
// order, which is what the trailing date does: both parts are fixed-width strings so the whole key
// compares as one.
const URGENCY_RANK = { overdue: 0, due30: 1, due60: 2, due90: 3, later: 4, complete: 5, unscheduled: 6 };

export function targetSortKey(m, today) {
  const rank = URGENCY_RANK[milestoneBucket(m, today)] ?? 9;
  return `${rank}|${ymd(m.targetDate) || '9999-99-99'}`;
}

// How far a milestone has moved from the date it was baselined at. Measured against where it
// actually landed if it's done, and where it's forecast to land if it isn't -- so an open milestone
// that has already slipped shows it now rather than once it completes. Positive is late. Null when
// there's no baseline to measure against, which is most of the portfolio until PMs start baselining.
export function slipDays(m) {
  const baseline = ymd(m.baselineDate);
  const landed = ymd(m.actualDate) || ymd(m.targetDate);
  if (!baseline || !landed) return null;
  return Math.round((Date.parse(landed + 'T00:00:00Z') - Date.parse(baseline + 'T00:00:00Z')) / 86400000);
}

// Committed money vs money that only bills if the customer uses or authorizes it. Anything with no
// value type set counts as committed, matching the backfill and the DB default-in-spirit.
export function valueTypeOf(m) {
  return m.valueType || DEFAULT_VALUE_TYPE;
}

export function contractCertainty(milestones) {
  let committed = 0;
  let contingent = 0;
  const contingentRows = new Map();
  for (const m of milestones) {
    const amount = amountOf(m);
    const type = valueTypeOf(m);
    if (CONTINGENT_VALUE_TYPES.includes(type)) {
      contingent += amount;
      const row = contingentRows.get(type) || { valueType: type, amount: 0, count: 0 };
      row.amount += amount;
      row.count += 1;
      contingentRows.set(type, row);
    } else {
      committed += amount;
    }
  }
  return {
    committed,
    contingent,
    rows: CONTINGENT_VALUE_TYPES.map((t) => contingentRows.get(t)).filter(Boolean),
  };
}

// Total contract value and how it splits by billing category (Judy, 2026-09-16). TCV is the whole
// contract -- every category, whatever the page is currently filtered to -- which is exactly why
// the cards above it can't answer this: they show the filtered scope, and the default hides
// License/Hosting/MA. Categories with nothing in them are dropped rather than shown as $0.
export function categoryTotals(milestones, categories) {
  const byCategory = new Map();
  let total = 0;
  for (const m of milestones) {
    const key = categoryOf(m);
    const amount = amountOf(m);
    total += amount;
    const row = byCategory.get(key) || { category: key, amount: 0, count: 0, startDate: null };
    row.amount += amount;
    row.count += 1;
    // Earliest target date in the category -- when this stream of billing starts. Shown on the
    // category card so it does not need repeating in a strip of its own.
    const target = ymd(m.targetDate);
    if (target && (!row.startDate || target < row.startDate)) row.startDate = target;
    byCategory.set(key, row);
  }
  // Fixed order, so the strip doesn't reshuffle as a filter changes; anything unrecognized
  // (Uncategorized, or a value added to the DB before the code knows about it) trails the list.
  const order = (c) => {
    const i = categories.indexOf(c.category);
    return i === -1 ? categories.length : i;
  };
  const rows = [...byCategory.values()].sort((a, b) => order(a) - order(b) || a.category.localeCompare(b.category));
  return { total, rows };
}

// When maintenance starts billing and what it's worth (Judy, 2026-09-16). The start is the earliest
// target date across everything labeled MA -- maintenance is the tail of the contract, so the first
// MA milestone is the handover point PS cares about.
//
// Deliberately reads the category off the milestone rather than taking a pre-filtered list: the
// tracker defaults to PS + R&D, which hides every MA row, and a strip that went blank under the
// default filter would be worse than not having one.
export function maSummary(milestones, category = 'MA') {
  const rows = milestones.filter((m) => m.billingCategory === category);
  const dates = rows.map((m) => ymd(m.targetDate)).filter(Boolean).sort();
  return {
    count: rows.length,
    amount: rows.reduce((sum, m) => sum + amountOf(m), 0),
    startDate: dates[0] || null,
  };
}

// Row tint (Judy, 2026-09-16): delivered green, past target red, inside 90 days yellow. Deliberately
// a wider band than the 30/60/90 cards -- the cards answer "what's landing next month", the tint
// answers "should I be looking at this row at all". Always paired with the Status badge and the
// Target Date text, so the state is never color-alone.
export function rowState(m, today) {
  if (isDone(m)) return 'done';
  const d = daysUntil(m.targetDate, today);
  if (d == null) return null;
  if (d < 0) return 'overdue';
  if (d <= 90) return 'dueSoon';
  return null;
}

export function periodKeyOf(dateStr, grain) {
  const s = ymd(dateStr);
  if (!s) return null;
  const year = s.slice(0, 4);
  const month = Number(s.slice(5, 7));
  return grain === 'quarter' ? `${year} Q${Math.floor((month - 1) / 3) + 1}` : `${year}-${s.slice(5, 7)}`;
}

// Both key shapes sort correctly as plain strings ("2026-03" < "2026-11", "2026 Q1" < "2026 Q4"),
// which is why the keys are built this way rather than as {year, month} pairs.
export function periodLabelOf(key, grain) {
  return grain === 'quarter' ? key : `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
}

// Short form for chart axes (Judy, 2026-09-16): "Q2 '23" and "Mar '26" rather than "2023 Q2" and
// "Mar 2026", so a whole contract's worth of quarters fits without shrinking the chart.
export function compactPeriodLabel(key, grain) {
  if (grain === 'quarter') return `${key.slice(5)} '${key.slice(2, 4)}`;
  return `${MONTHS[Number(key.slice(5, 7)) - 1]} '${key.slice(2, 4)}`;
}

// Every period between the earliest and latest milestone in scope, including empty ones -- a gap
// month has to hold its place or the burn-down's x-axis lies about elapsed time.
//
// `dateFns` is which dates have to fit inside the range. The pivot passes the default, because it
// places each row in exactly one period. The burn-down passes target dates too: a milestone
// delivered early sits at its actual date, and if its later target fell outside the range the
// planned line could never burn down past it (it stayed flat at full scope forever).
export function periodRange(milestones, grain, dateFns = [periodDateOf]) {
  const keys = milestones.flatMap((m) => dateFns.map((fn) => periodKeyOf(fn(m), grain))).filter(Boolean);
  if (keys.length === 0) return [];
  const first = keys.reduce((a, b) => (a < b ? a : b));
  const last = keys.reduce((a, b) => (a > b ? a : b));
  const out = [];
  if (grain === 'quarter') {
    let [y, q] = [Number(first.slice(0, 4)), Number(first.slice(6))];
    const [ly, lq] = [Number(last.slice(0, 4)), Number(last.slice(6))];
    while (y < ly || (y === ly && q <= lq)) {
      out.push(`${y} Q${q}`);
      if (q === 4) { q = 1; y += 1; } else { q += 1; }
    }
  } else {
    let [y, mo] = [Number(first.slice(0, 4)), Number(first.slice(5, 7))];
    const [ly, lmo] = [Number(last.slice(0, 4)), Number(last.slice(5, 7))];
    while (y < ly || (y === ly && mo <= lmo)) {
      out.push(`${y}-${String(mo).padStart(2, '0')}`);
      if (mo === 12) { mo = 1; y += 1; } else { mo += 1; }
    }
  }
  // One mistyped year is enough to ask for thousands of columns -- a real actualDate of 0206-06-16
  // (for 2026) had the burn-down drawing every quarter since the third century, which made both it
  // and the calendar view unreadable. 40 years is far past any real engagement, so this only ever
  // fires on bad data, and it keeps the recent end, which is the part either view is about.
  const MAX_PERIODS = grain === 'quarter' ? 160 : 480;
  return out.length > MAX_PERIODS ? out.slice(-MAX_PERIODS) : out;
}

export function amountOf(m) {
  return Number(m.amount || 0);
}

// Year-over-year FCST/ACT split. Years come from the same date that places a milestone in the grid,
// so a row can't total into one year here and appear under another in the calendar view.
export function yearTotals(milestones) {
  const byYear = new Map();
  for (const m of milestones) {
    const year = yearOf(periodDateOf(m)) ?? 'Unscheduled';
    if (!byYear.has(year)) byYear.set(year, { year, fcst: 0, act: 0, fcstCount: 0, actCount: 0 });
    const row = byYear.get(year);
    const amt = amountOf(m);
    if (trackOf(m) === 'ACT') { row.act += amt; row.actCount += 1; } else { row.fcst += amt; row.fcstCount += 1; }
  }
  // Unscheduled last; every real year ascending.
  return [...byYear.values()].sort((a, b) => {
    if (a.year === 'Unscheduled') return 1;
    if (b.year === 'Unscheduled') return -1;
    return a.year.localeCompare(b.year);
  });
}

// Which rows a clicked summary card should show. Most cards map straight onto a bucket; the
// This Month card's three are about the month rather than the aging bands, and 'remaining' spans
// several buckets, so they're handled here rather than by milestoneBucket.
//
// Keeping this in one place means the grid can't drift from what the card counted.
export function matchesCardFilter(m, filter, today) {
  if (!filter) return true;
  const targetsThisMonth = ymd(m.targetDate).slice(0, 7) === ymd(today).slice(0, 7);
  switch (filter) {
    case 'remaining': return !isDone(m);
    case 'month': return targetsThisMonth;
    case 'monthHit': return targetsThisMonth && isDone(m);
    case 'monthOpen': return targetsThisMonth && !isDone(m);
    default: return milestoneBucket(m, today) === filter;
  }
}

// Milestone attainment for one calendar month (Judy, 2026-09-16) -- the exec number: how many did
// we say we'd hit, how many did we hit, what percent.
//
// Definitions, because this gets reported upward and has to mean one thing:
//   planned   -- milestones whose TARGET date falls in the month. The commitment.
//   hit       -- of those, the ones that are done. Delivered early still counts as hit: the
//                commitment was met, and penalising early delivery would be perverse.
//   missed    -- planned and not yet done.
//   unplanned -- done in the month but targeted at some other month. Real delivery, but it isn't
//                attainment against the plan, so it's reported separately rather than inflating it.
//
// dayProgress is how far through the month we are, so a month in flight can be read against where
// it should be rather than looking like a miss on the 3rd.
export function monthAttainment(milestones, today) {
  const month = ymd(today).slice(0, 7);
  const inMonth = (d) => ymd(d).slice(0, 7) === month;

  const planned = milestones.filter((m) => inMonth(m.targetDate));
  const hit = planned.filter(isDone);
  const missed = planned.filter((m) => !isDone(m));
  const unplanned = milestones.filter((m) => isDone(m) && inMonth(periodDateOf(m)) && !inMonth(m.targetDate));

  const sum = (rows) => rows.reduce((total, m) => total + amountOf(m), 0);
  const day = Number(ymd(today).slice(8, 10));
  const daysInMonth = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();

  return {
    month,
    plannedCount: planned.length,
    hitCount: hit.length,
    missedCount: missed.length,
    unplannedCount: unplanned.length,
    plannedAmount: sum(planned),
    hitAmount: sum(hit),
    unplannedAmount: sum(unplanned),
    missed,
    // Null rather than 0 when nothing was planned -- "0% achieved" and "nothing was due" are very
    // different things to put in front of an exec.
    pct: planned.length ? Math.round((hit.length / planned.length) * 100) : null,
    pctAmount: sum(planned) ? Math.round((sum(hit) / sum(planned)) * 100) : null,
    dayProgress: Math.round((day / daysInMonth) * 100),
  };
}

// Burn-down: how much contracted value is still outstanding at the end of each period.
//   planned  -- what the schedule says should be left, from target dates
//   actual   -- what is really left, from completed milestones, and only up to the current period
// Two series on one scale, so the gap between them is the slippage. Milestones with no date at all
// stay in `scope` but never burn down, which is the honest treatment: they're outstanding work.
export function burndownSeries(milestones, grain, today) {
  // The range has to reach today, not just the last dated milestone: a done-but-undated milestone
  // retires in the current period (see below), and a period that isn't in the range can never
  // retire anything. Anchored on the milestones though -- with no dates at all there is nothing to
  // chart, and inventing a single period from today would draw a line out of thin air.
  const anchored = periodRange(milestones, grain, [periodDateOf, (m) => m.targetDate]);
  const periods = anchored.length === 0
    ? []
    : periodRange([...milestones, { targetDate: today }], grain, [periodDateOf, (m) => m.targetDate]);
  if (periods.length === 0) return { periods: [], scope: 0, points: [] };
  const scope = milestones.reduce((sum, m) => sum + amountOf(m), 0);
  const currentPeriod = periodKeyOf(today, grain);

  const points = periods.map((key) => {
    let plannedDone = 0;
    let actualDone = 0;
    // Per-period, not cumulative: what the chart's dots and tooltips report, and what makes a flat
    // stretch legible as "nothing was billed here" rather than just a line that didn't move.
    let delivered = 0;
    let due = 0;
    const deliveredTitles = [];
    for (const m of milestones) {
      const amt = amountOf(m);
      const target = periodKeyOf(m.targetDate, grain);
      // A completed milestone carrying no date at all is still completed, but it could never land
      // in a period, so it was never subtracted and sat in "remaining" forever (Jeremy, PR #134).
      // It falls back to the CURRENT period rather than the first: we know it's done as of now and
      // nothing more, and retiring it at the start of the contract would invent a delivery date and
      // understate what was genuinely outstanding back then. An *open* undated milestone still
      // stays outstanding in both lines, because it is.
      const done = isDone(m) ? (periodKeyOf(periodDateOf(m), grain) || currentPeriod) : null;

      if ((target && target <= key) || (done && !target && done <= key)) plannedDone += amt;
      if (target === key) due += amt;
      if (isDone(m)) {
        if (done && done <= key) actualDone += amt;
        if (done === key) { delivered += amt; deliveredTitles.push(m.title); }
      }
    }
    const isFuture = !!(currentPeriod && key > currentPeriod);
    return {
      key,
      planned: scope - plannedDone,
      // Past the current period there is no actual yet -- the line stops rather than flat-lining,
      // which would read as "nothing more was delivered" instead of "we aren't there yet".
      actual: isFuture ? null : scope - actualDone,
      delivered,
      due,
      deliveredTitles,
      isFuture,
    };
  });
  return { periods, scope, points };
}

// Rows x periods for the calendar/pivot view, plus the column totals the bottom row shows.
// Runs of consecutive elapsed periods where nothing at all was billed. The point Judy wants the
// chart to make (2026-09-16): a long flat stretch is the thing to notice, and a flat line alone
// doesn't say it loudly enough. Only runs of `minLength`+ are worth marking -- a single quiet month
// is normal. Future periods are excluded: nothing being billed yet isn't a gap.
export function noBillingRuns(points, minLength = 3) {
  const runs = [];
  let start = null;
  const close = (endIndex) => {
    if (start != null && endIndex - start + 1 >= minLength) {
      runs.push({ startIndex: start, endIndex, length: endIndex - start + 1 });
    }
    start = null;
  };
  points.forEach((p, i) => {
    if (!p.isFuture && p.delivered === 0) {
      if (start == null) start = i;
    } else {
      close(i - 1);
    }
  });
  close(points.length - 1);
  return runs;
}

export function pivot(milestones, grain) {
  const periods = periodRange(milestones, grain);
  const rows = milestones.map((m) => ({
    milestone: m,
    periodKey: periodKeyOf(periodDateOf(m), grain),
    amount: amountOf(m),
  }));
  const totals = {};
  for (const key of periods) totals[key] = 0;
  let unscheduled = 0;
  for (const row of rows) {
    if (row.periodKey && totals[row.periodKey] != null) totals[row.periodKey] += row.amount;
    else unscheduled += row.amount;
  }
  // A recurring line is one display row spread across its months (totals above still count each month
  // once). `cells` is period -> amount, so the grid needn't assume a row has a single period.
  const display = [];
  const series = new Map();
  for (const row of rows) {
    const parent = row.milestone.recurringParentId;
    if (!parent) { display.push({ ...row, cells: row.periodKey ? { [row.periodKey]: row.amount } : {} }); continue; }
    let d = series.get(parent);
    if (!d) { d = { milestone: null, periodKey: null, amount: 0, cells: {}, members: [] }; series.set(parent, d); display.push(d); }
    d.members.push(row.milestone);
    d.amount += row.amount;
    if (row.periodKey) d.cells[row.periodKey] = (d.cells[row.periodKey] || 0) + row.amount;
  }
  for (const d of series.values()) d.milestone = collapseRecurring(d.members)[0];
  return { periods, rows: display, totals, unscheduled };
}

// ---------- Recurring milestones (one row by rule) ----------

// Mirror of recurrencePeriods in packages/shared (web doesn't import it). One period per month from
// start through end inclusive on the start's day of month; the last period absorbs the cent remainder.
export function recurrencePeriods(start, end, total, completed) {
  const [sy, sm, sd] = [Number(start.slice(0, 4)), Number(start.slice(5, 7)), Number(start.slice(8, 10))];
  const [ey, em] = [Number(end.slice(0, 4)), Number(end.slice(5, 7))];
  const count = (ey - sy) * 12 + (em - sm) + 1;
  if (!(count >= 1) || count > 240) return [];
  const each = Math.floor((total / count) * 100) / 100;
  return Array.from({ length: count }, (_, i) => {
    const m0 = sm - 1 + i;
    const y = sy + Math.floor(m0 / 12);
    const mo = (m0 % 12) + 1;
    const day = Math.min(sd, new Date(Date.UTC(y, mo, 0)).getUTCDate());
    const date = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const amount = i === count - 1 ? Math.round((total - each * (count - 1)) * 100) / 100 : each;
    return { index: i + 1, date, amount, done: i < completed };
  });
}

/**
 * Turns a recurring milestone into one row per billing period, so every total, the pivot and the
 * burn-down see the real monthly money (same approach as expandDrawdowns). The grid collapses them
 * back to one row with collapseRecurring. A completed period lands on its own period date -- the rule
 * has no per-month actual by design.
 */
export function expandRecurring(milestones) {
  const out = [];
  for (const m of milestones) {
    const periods = m.recurrence && m.targetDate && m.recurrenceEnd
      ? recurrencePeriods(ymd(m.targetDate), ymd(m.recurrenceEnd), amountOf(m), m.periodsCompleted || 0)
      : [];
    if (!periods.length) { out.push(m); continue; }
    for (const p of periods) {
      out.push({
        ...m,
        id: `${m.id}::period::${p.index}`,
        title: `${m.title} — ${monthYearLabel(p.date)}`,
        amount: p.amount,
        targetDate: p.date,
        actualDate: p.done ? p.date : null,
        status: p.done ? 'Accepted' : 'Not Started',
        // A series has one baseline for the whole line; a single month inheriting it would report
        // every later month as hugely late.
        baselineDate: null,
        recurringParentId: m.id,
        recurringTitle: m.title,
        periodIndex: p.index,
        periodCount: periods.length,
      });
    }
  }
  return out;
}

// One grid row per series: Target is the next open period (the last one once all are done), Actual the
// last completed one, Amount the sum of whichever of its periods survived the current filters.
export function collapseRecurring(rows) {
  const out = [];
  const groups = new Map();
  for (const r of rows) {
    if (!r.recurringParentId) { out.push(r); continue; }
    let g = groups.get(r.recurringParentId);
    if (!g) { g = { first: r, rows: [] }; groups.set(r.recurringParentId, g); out.push(g); }
    g.rows.push(r);
  }
  return out.map((o) => {
    if (!o.rows) return o;
    const { first, rows: periods } = o;
    const done = first.periodsCompleted || 0;
    const open = periods.filter((p) => !isDone(p));
    const completed = periods.filter(isDone);
    return {
      ...first,
      id: first.recurringParentId,
      title: `${first.recurringTitle} (monthly · ${done} of ${first.periodCount} done)`,
      amount: periods.reduce((sum, p) => sum + amountOf(p), 0),
      targetDate: (open[0] || periods[periods.length - 1]).targetDate,
      actualDate: completed.length ? completed[completed.length - 1].actualDate : null,
      status: done === 0 ? 'Not Started' : done >= first.periodCount ? 'Accepted' : 'In Progress',
      isRecurringSummary: true,
    };
  });
}

// ---------- Billed-as-used drawdown (ADR 0046) ----------

// Mirror of DRAWDOWN_FCST_MODE in packages/shared -- apps/web has no dependency on it to import.
// 'reduce': the pool milestone keeps only its undrawn balance, so FCST + ACT equals the contract.
// 'full':   the pool milestone keeps its whole amount and the draws sit on top of it.
// OPEN: Jeremy asked Judy 2026-10-06. Flipping this constant is the entire switch.
export const DRAWDOWN_FCST_MODE = 'reduce';

/**
 * Turns a milestone with a linked billed-as-used pot into one row per drawn line plus the pool row.
 *
 * A drawdown milestone is the one case where a single contract line bills on several dates: the
 * money leaves the pot as each deliverable is accepted, and the DAS submitted date is when that
 * happened. Collapsing it to one lump at the milestone's target date is what the old sheet did, and
 * it put revenue in the wrong month.
 *
 * Applied once at load, so every reader -- cards, pivot, burn-down, YoY, exports -- sees the same
 * rows and can't disagree about what was billed when.
 */
export function expandDrawdowns(milestones, mode = DRAWDOWN_FCST_MODE) {
  const out = [];
  for (const m of milestones) {
    const dd = m.drawdown;
    if (!dd || !dd.draws.length) { out.push(m); continue; }

    for (const draw of dd.draws) {
      out.push({
        ...m,
        // Prefixed with the parent id so a draw can never collide with a real milestone's row key,
        // and so the pool it came from is recoverable from the row alone.
        id: `${m.id}::draw::${draw.id}`,
        title: `${m.title} — ${draw.label}`,
        amount: draw.amount,
        // Recognized at the DAS submitted date. A draw with no DAS date yet falls back to the
        // milestone's target so it still appears, rather than silently dropping out of the totals.
        actualDate: draw.dasSubmittedDate || null,
        targetDate: draw.dasSubmittedDate || m.targetDate,
        // Both are done statuses, so a draw always reads as ACT; the split just preserves whether
        // it's been invoiced, which is the register's Billed? column.
        status: draw.billed ? 'Billed' : 'Accepted',
        dasNumber: draw.dasNumber || m.dasNumber,
        // Slip measures a date against its baseline; a draw has no baseline of its own, and
        // inheriting the pool's would report every draw as late by however long the pot has run.
        baselineDate: null,
        drawdown: null,
        isDraw: true,
        poolMilestoneId: m.id,
      });
    }

    const reduced = mode === 'reduce' && dd.drawn > 0;
    out.push({
      ...m,
      amount: reduced ? dd.remaining : dd.pool,
      // Said in the title rather than only in a column: this row's amount is no longer the contract
      // figure, and a reader scanning the grid has to be able to see that without a legend.
      title: reduced ? `${m.title} — undrawn balance` : m.title,
      isDrawdownPool: true,
    });
  }
  return out;
}
